import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { useCompleteBenefitsUserOnboardingMutation } from '../services/userOnboardingApi';
import { VOUCHER_ONBOARDING_TYPE_WELCOME } from '../types';
import { useEmissorBenefitsOnboardingEntry } from './useEmissorBenefitsOnboardingEntry';
function isHttpConflict(error) {
    if (typeof error !== 'object' || error === null || !('status' in error)) {
        return false;
    }
    return error.status === 409 || error.status === '409';
}
export function useHomePageEmissorBenefitsWelcome({ hasActivatedEmissorContract, emissorContractId, }) {
    const { t } = useTranslation();
    const { shouldShowOnboardingEntry } = useEmissorBenefitsOnboardingEntry({
        hasActivatedEmissorContract,
    });
    const [completeBenefitsUserOnboarding] = useCompleteBenefitsUserOnboardingMutation();
    const welcomeOpenPersistedRef = useRef(false);
    const entryOpenedRef = useRef(false);
    const [step, setStep] = useState('idle');
    useEffect(() => {
        welcomeOpenPersistedRef.current = false;
        entryOpenedRef.current = false;
        setStep('idle');
    }, [emissorContractId]);
    useEffect(() => {
        if (shouldShowOnboardingEntry && step === 'idle' && !entryOpenedRef.current) {
            entryOpenedRef.current = true;
            setStep('entry');
        }
    }, [shouldShowOnboardingEntry, step]);
    useEffect(() => {
        if (step !== 'entry' || welcomeOpenPersistedRef.current) {
            return;
        }
        welcomeOpenPersistedRef.current = true;
        completeBenefitsUserOnboarding({ type: VOUCHER_ONBOARDING_TYPE_WELCOME })
            .unwrap()
            .catch((error) => {
            if (isHttpConflict(error)) {
                return;
            }
            welcomeOpenPersistedRef.current = false;
        });
    }, [step, completeBenefitsUserOnboarding]);
    const startOnboardingFromWelcome = useCallback(() => {
        setStep('tour');
    }, []);
    const finishOnboarding = useCallback(() => {
        setStep('idle');
    }, []);
    const isOnboardingEntryOpen = step === 'entry';
    const isOnboardingPreviewActive = step === 'entry' || step === 'tour' || step === 'skip';
    const welcomeModalProps = {
        isOpen: isOnboardingEntryOpen,
        onClose: () => setStep('skip'),
        onPrimaryAction: startOnboardingFromWelcome,
        closeOnPrimaryAction: false,
        title: t('benefits.onboarding.intro.title'),
        description: t('benefits.onboarding.intro.description'),
        primaryActionLabel: t('benefits.onboarding.intro.start'),
        primaryActionLeftIcon: 'none',
        primaryActionRightIcon: 'arrowLineRight',
        onboardingWelcomeActions: true,
        secondaryActionLabel: t('benefits.onboarding.intro.skip'),
        onSecondaryAction: () => setStep('skip'),
    };
    const benefitsOnboardingWizard = step === 'tour' || step === 'skip'
        ? {
            mode: step === 'tour' ? 'tour' : 'skip',
            onReopenFromSkipCancel: () => setStep('entry'),
            onFlowFinished: finishOnboarding,
        }
        : null;
    return {
        welcomeModalProps,
        benefitsOnboardingWizard,
        isOnboardingPreviewActive,
    };
}
